/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react'
import { Card, CardContent, Typography, Grid } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import { PieChart as PieChartGraph } from '@mui/x-charts/PieChart'
import { type DefaultizedPieValueType } from '@mui/x-charts'

export default function PieChart({
  data,
  title,
  width = 0,
  subtitle = '',
  sxFont = {},
  typeTotal = '',
  info = '',
  showPercent = false,
  legendPosition = 'right',
  compactLegend = false,
}) {
  const { translate } = useLang()

  const totalValue = data.reduce((sum, item) => sum + item.value, 0)
  const getValueFormatter = (params) => {
    const value = params.value
    const percentage =
      totalValue > 0 ? ((value / totalValue) * 100).toFixed(0) : 0
    return `${params.total} (${percentage}%)`
  }

  const getArcLabel = (params: DefaultizedPieValueType) => {
    if (!showPercent) return ''
    const percentage =
      totalValue > 0 ? ((params.value / totalValue) * 100).toFixed(0) : 0
    return `${percentage}%`
  }

  const cardMinHeight =
    legendPosition === 'bottom'
      ? data.length <= 5
        ? 500
        : Math.max(600, 420 + data.length * 9)
      : 650

  return (
    <Card
      variant="outlined"
      sx={{ overflowY: 'auto', height: '100%', minHeight: cardMinHeight }}
    >
      <CardContent sx={{ height: '80%' }}>
        <Typography sx={{ fontWeight: 'bold', ...sxFont }}>
          {translate(title)}
        </Typography>
        {info && (
          <Typography variant="subtitle2" marginBottom={2} marginTop={1}>
            {translate(info)}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="subtitle2" marginBottom={2} marginTop={1}>
            {translate(subtitle)}
            {typeTotal &&
              ` | ${translate('Total of')} ${translate(typeTotal)}: ${
                data.length
              }`}
          </Typography>
        )}
        {data.length !== 0 ? (
          <Grid
            container
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <PieChartGraph
              series={[
                {
                  data: data,
                  valueFormatter: getValueFormatter,
                  arcLabel: getArcLabel,
                  outerRadius: 150,
                  arcLabelMinAngle: 35,
                },
              ]}
              sx={
                compactLegend && data.length >= 30
                  ? {
                      '& .MuiChartsArcLabel-root': { fontSize: '10px' },
                      '& .MuiChartsLegend-label': {
                        fontSize: '10px !important',
                      },
                    }
                  : undefined
              }
              width={width !== 0 ? width : undefined}
              height={
                legendPosition === 'bottom'
                  ? data.length <= 5
                    ? 480
                    : Math.max(580, 420 + data.length * 9)
                  : data.length > 20
                  ? 700
                  : 500
              }
              margin={{
                bottom:
                  legendPosition === 'bottom'
                    ? data.length <= 5
                      ? 120
                      : Math.max(130, Math.ceil(data.length / 4) * 22 + 40)
                    : 100,
                left: 100,
                right: legendPosition === 'right' ? data.length * 10 : 100,
                top: 50,
              }}
              slotProps={{
                legend: {
                  direction: legendPosition === 'bottom' ? 'row' : 'column',
                  position:
                    legendPosition === 'bottom'
                      ? { vertical: 'bottom', horizontal: 'middle' }
                      : { vertical: 'middle', horizontal: 'right' },
                  ...(compactLegend && data.length >= 30
                    ? {
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                        markGap: 4,
                        itemGap: 4,
                      }
                    : {}),
                },
              }}
              skipAnimation
            />
          </Grid>
        ) : (
          <Grid item sx={{ height: '100%' }}>
            <Card
              variant="outlined"
              sx={{
                display: 'flex',
                height: '100%',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
              }}
            >
              <CardContent>
                <Typography>{translate('No data available')}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </CardContent>
    </Card>
  )
}
